/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react';

import debounce from 'lodash/debounce';

import { AsyncSelect, components } from '@atlaskit/select';

import {
  ProjectIconStyled,
  ProjectOptionStyled,
} from '@qality/common-components/src/select/RecentAndMoreProjectSelect.styled';
import { SelectActionButton } from '@qality/common-components/src/select/select-components/SelectActionButton';
import { SelectHint } from '@qality/common-components/src/select/select-components/SelectHint';
import {
  ValueContainerWithOverflow,
  ValueContainerWithOverflowStyles,
} from '@qality/common-components/src/select/select-components/ValueContainerWithOverflow';
import { DEBOUNCE_DELAY_MS } from '@qality/common-config';
import {
  ProjectSelectOption,
  ProjectsAvailabilityByProjectKey,
} from '@qality/common-model';
import { StorageOfUniqueServices } from '@qality/common-service';

import {
  ProjectSelectGroupedOption,
  ProjectSelectGroupedOptions,
} from './RecentAndMoreProjects.d';
import {
  addProjectFromUrl,
  addProjectFromValue,
  mapMoreProjectsToSelectOptions,
  mapProjectsToSelectOptions,
} from './RecentAndMoreProjects.util';

type Props = {
  onChange: (option: ProjectSelectOption) => Promise<void>;
  onBlur: (event: React.FocusEvent<HTMLElement>) => void;
  isClearable: boolean;
  value?: ProjectSelectOption;
  isDisabled: boolean;
  isInvalid: boolean;
  placeholder?: string;
  projectsAvailabilityByProjectKey: ProjectsAvailabilityByProjectKey;
  projectId?: string;
  isInModal: boolean;
  isMulti?: boolean;
  optionsLimit?: number;
};

const ERROR_MESSAGE = [
  'We could not retrieve project details.',
  'Please try again. If the problem persists, feel free to contact our support.',
].join(' ');

const ERROR_MESSAGE_DEFAULT = [
  'We could not retrieve default project details.',
  'Please try again. If the problem persists, feel free to contact our support.',
].join(' ');

const ERROR_MESSAGE_LOAD_MORE = [
  'We could not load more projects.',
  'Please try again. If the problem persists, feel free to contact our support.',
].join(' ');

const NUMBER_OF_PROJECTS_TO_LOAD_MORE = 20;

export default function RecentAndMoreProjectSelect({
  onChange,
  onBlur,
  isClearable,
  value,
  isDisabled = false,
  isInvalid,
  placeholder,
  projectsAvailabilityByProjectKey,
  projectId,
  isInModal,
  isMulti,
}: Props): JSX.Element {
  const [selectDefaultOptions, setSelectDefaultOptions] =
    useState<ProjectSelectGroupedOptions>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInputEmpty, setIsInputEmpty] = useState(true);

  useEffect(() => {
    initOptions();
  }, []);

  const initOptions = async (): Promise<void> => {
    try {
      const mappedProjects = await loadOptions('');
      if (value) {
        const projects = addProjectFromValue(mappedProjects, value);
        setSelectDefaultOptions(projects);
      } else if (projectId === 'ALL_PROJECTS') {
        await onChange({ label: 'All projects', value: 'ALL_PROJECTS' });
        setSelectDefaultOptions(mappedProjects);
      } else if (projectId) {
        const { projects, project } = await addProjectFromUrl(
          mappedProjects,
          projectId
        );

        setSelectDefaultOptions(projects);
        await onChange(project);
      } else {
        setSelectDefaultOptions(mappedProjects);
      }
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        ERROR_MESSAGE_DEFAULT
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loadOptions = async (
    inputValue: string
  ): Promise<ProjectSelectGroupedOptions> => {
    const params = {
      query: inputValue,
      filterByQAlitySettings: true,
      projectsAvailabilityByProjectKey,
    };

    try {
      const projects = await StorageOfUniqueServices.get(
        'ProjectServiceUnique'
      ).getRecentAndMoreProjects(params);

      return mapProjectsToSelectOptions(projects);
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        ERROR_MESSAGE
      );
    }
  };

  const loadMoreOptions = async (): Promise<void> => {
    setIsLoading(true);

    const [recentProjectsGroup, moreProjectsGroup] = selectDefaultOptions;
    const recentProjectsLength = recentProjectsGroup?.options?.length ?? 0;
    const moreProjectsLength = moreProjectsGroup?.options?.length ?? 0;

    const currentProjectsTotalLength =
      recentProjectsLength + moreProjectsLength;
    // NOTE: The final projects total length might be shorter
    // eg. when all available projects are fetched
    const newWantedProjectsTotalLength =
      currentProjectsTotalLength + NUMBER_OF_PROJECTS_TO_LOAD_MORE;

    const params = {
      query: '',
      limit: newWantedProjectsTotalLength,
      filterByQAlitySettings: true,
      projectsAvailabilityByProjectKey,
    };

    try {
      const newMoreProjects = await StorageOfUniqueServices.get(
        'ProjectServiceUnique'
      ).getProjectsPaginated(params);

      const newDefaultSelectOptions = mapMoreProjectsToSelectOptions(
        newMoreProjects,
        selectDefaultOptions,
        {
          currentRecent: recentProjectsLength,
          currentMore: moreProjectsLength,
          wantedTotal: newWantedProjectsTotalLength,
        }
      );

      setSelectDefaultOptions(newDefaultSelectOptions);
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        ERROR_MESSAGE_LOAD_MORE
      );
    } finally {
      setIsLoading(false);
    }
  };

  const _loadOptions = (
    inputValue: string,
    callback: (options: ProjectSelectGroupedOptions) => void
  ): void => {
    // lodash/debounce doesn't work with async/await
    // eslint-disable-next-line promise/catch-or-return, promise/no-callback-in-promise
    loadOptions(inputValue).then(callback);
  };

  const loadOptionsDebounced = debounce(_loadOptions, DEBOUNCE_DELAY_MS);

  const handleOnInputChange = (inputValue: string): void => {
    setIsInputEmpty(inputValue?.length === 0);
  };

  return (
    <AsyncSelect
      isDisabled={isDisabled}
      testId='project-select'
      className='project-select'
      classNamePrefix='solkit'
      defaultOptions={selectDefaultOptions}
      loadOptions={loadOptionsDebounced}
      placeholder={placeholder}
      onChange={onChange}
      onInputChange={handleOnInputChange}
      onBlur={onBlur}
      isClearable={isClearable}
      value={value}
      validationState={isInvalid ? 'error' : undefined}
      noOptionsMessage={() => 'No available projects'}
      isLoading={isLoading}
      isMulti={isMulti}
      closeMenuOnSelect={!isMulti}
      formatOptionLabel={(option: ProjectSelectOption) => {
        return (
          <ProjectOptionStyled>
            {option.iconUrl && <ProjectIconStyled src={option?.iconUrl} />}
            {option.label}
          </ProjectOptionStyled>
        );
      }}
      components={{
        ValueContainer: ValueContainerWithOverflow,
        MenuList: (props) => {
          return (
            <components.MenuList {...props}>
              <SelectHint text='Use textfield to search for project' />
              {props!.children}
            </components.MenuList>
          );
        },
        Group: (props) => {
          const getGroupFooter = () => {
            const data = props!.data as ProjectSelectGroupedOption;
            if (data.label === 'More projects' || data.label === '') {
              if (!isInputEmpty) {
                return null;
              }

              if (data.areMoreProjectsAvailable) {
                return (
                  <SelectActionButton
                    appearance='link'
                    isLoading={isLoading}
                    onClick={loadMoreOptions}
                  >
                    Load more projects
                  </SelectActionButton>
                );
              }

              return <SelectHint text='No more projects to load' />;
            }
          };

          return (
            <components.Group {...props}>
              {props!.children}
              {getGroupFooter()}
            </components.Group>
          );
        },
      }}
      menuPortalTarget={isInModal && document.body}
      styles={{
        multiValue: (base) => ({
          ...base,
          ...ValueContainerWithOverflowStyles,
        }),
        menuPortal: (base) => {
          if (isInModal) {
            return {
              ...base,
              zIndex: 9999,
              transform: 'translate(-10px)',
            };
          }
          return base;
        },
      }}
    />
  );
}
